import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { KAvatar, KCheckbox, KTable } from "@kyburz/kdesign-system";
import { getUsers } from "../../apiCalls/admin/users";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import useSetState from "../../hooks/useSetState";
import { GroupData } from "../../interfaces/GroupInterface";

const useStyles = makeStyles(() => ({
  root: {
    height: "calc(100vh - 465px)",
  },
}));
interface Props {
  groupData: GroupData;
  activeFilter: "inkl" | "notInkl" | "all";
  handleReset: () => void;
  reset: boolean;
  handleCheckedIds: (name: string, ids: string[]) => void;
  updateUsersLength: (length: number) => void;
}

const GroupViewUsersTable = ({
  groupData,
  activeFilter,
  handleReset,
  reset,
  handleCheckedIds,
  updateUsersLength,
}: Props) => {
  const classes = useStyles();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );

  const [
    {
      items,
      filteredItems,
      preSelected,
      userIds,
      canReserveIds,
      loading,
      columnsToRender,
    },
    setState,
  ] = useSetState({
    items: [],
    filteredItems: [],
    preSelected: {},
    userIds: [],
    canReserveIds: [],
    loading: false,
    columnsToRender: [],
  });

  const setSelectedRows = (
    isAllSelected: any,
    selectedRows: any,
    latestSelected: any,
  ): void => {
    if (!reset) {
      const latestSelectedId = filteredItems[latestSelected]?.id;
      if (latestSelected === "all") {
        const ids = Object.keys(selectedRows).map((x) => filteredItems[x]?.id);
        const mergedWithoutDuplicates = [...new Set([...userIds, ...ids])];
        if (mergedWithoutDuplicates?.length > userIds?.length) {
          setState({ userIds: mergedWithoutDuplicates });
        } else {
          setState({ userIds: ids });
        }
      } else if (userIds.includes(latestSelectedId)) {
        const removed = userIds.filter((x) => x !== latestSelectedId);
        setState({ userIds: removed });
      } else {
        const merged = userIds.concat(latestSelectedId);
        setState({ userIds: merged });
      }
    } else {
      handleReset();
    }
  };

  const handleCanReserve = (userId: string): void => {
    const filteredOut = canReserveIds.filter((x) => x !== userId);
    setState({
      canReserveIds: canReserveIds.includes(userId)
        ? filteredOut
        : [...canReserveIds, userId],
    });
  };

  useEffect(() => {
    const initial = [
      {
        Header: "Avatar",
        width: 50,
        Cell: (row) => {
          return (
            <div style={{ marginTop: -4 }}>
              <KAvatar
                avatar={row.cell.row.original.avatar}
                firstName={row.cell.row.original.first_name}
                lastName={row.cell.row.original.last_name}
                size={28}
              />
            </div>
          );
        },
        defaultCanFilter: false,
      },
      {
        Header: "First Name",
        accessor: "first_name",
        defaultCanSort: false,
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        defaultCanSort: false,
      },
      {
        Header: "Email",
        accessor: "email",
        defaultCanSort: false,
      },
    ];
    if (currentCustomer?.shortname === "trav") {
      setState({
        columnsToRender: [
          ...initial,
          {
            Header: "Can Reserve",
            width: 100,
            Cell: (row) => {
              const canReserveCheckedId: string = row?.cell?.row?.original?.id;
              return (
                <div style={{ marginTop: -4 }}>
                  <KCheckbox
                    handleChange={() => handleCanReserve(canReserveCheckedId)}
                    name="canReserve"
                    checked={canReserveIds.includes(canReserveCheckedId)}
                  />
                </div>
              );
            },
            defaultCanFilter: false,
          },
        ],
      });
    } else {
      setState({ columnsToRender: initial });
    }
  }, [currentCustomer, canReserveIds]);

  useEffect(() => {
    if (filteredItems?.length && userIds) {
      const reducedItems: { index: boolean } = filteredItems
        .map((x, index: boolean) => (userIds.includes(x?.id) ? index : ""))
        .filter(String)
        .reduce((obj, item: number) => ({ ...obj, [item]: true }), {});
      setState({ preSelected: reducedItems });
    }
  }, [filteredItems, userIds, activeFilter]);

  useEffect(() => {
    if (items?.length && activeFilter) {
      if (activeFilter === "inkl") {
        setState({
          filteredItems: items.filter((x) =>
            groupData?.users?.map((y: any) => y?.user?.user_id).includes(x?.id),
          ),
        });
      } else if (activeFilter === "notInkl") {
        setState({
          filteredItems: items.filter(
            (x) =>
              !groupData?.users
                ?.map((y: any) => y?.user?.user_id)
                .includes(x?.id),
          ),
        });
      } else {
        setState({ filteredItems: items });
      }
    }
  }, [activeFilter, items]);

  useEffect(() => {
    if (groupData?.users?.length) {
      setState({ userIds: groupData.users.map((x) => x?.user?.user_id) });
    }
  }, [groupData.users]);

  useEffect(() => {
    handleCheckedIds("userIds", userIds);
  }, [userIds]);

  useEffect(() => {
    handleCheckedIds("canReserveIds", canReserveIds);
  }, [canReserveIds]);

  // Temporary fix to filter users with first names

  useEffect(() => {
    setState({ loading: true });
    getUsers(currentCustomer?.shortname)
      .then((res) => {
        setState({
          items: res?.map((x) => ({
            id: x._id,
            keycloakId: x._id,
            first_name: x.first_name || "",
            last_name: x.last_name || "",
            email: x.email,
            username: x.username,
            registration_status: x.registration_status,
            avatar: x.avatar,
            role: x.role?.name || "no role assigned",
          })),
          loading: false,
        });
      })
      .catch((e) => {
        console.error(e);
        setState({ loading: false });
      });
  }, []);

  useEffect(() => {
    if (groupData.name) {
      setState({
        canReserveIds: groupData?.users
          ?.filter((x) => x.create_reservation)
          .map((x) => x.user.user_id),
      });
    }
  }, [groupData]);

  useEffect(() => {
    if (items.length) {
      updateUsersLength(items.length);
    }
  }, [items]);

  return (
    <div className={classes.root}>
      {loading ? (
        <>
          <Skeleton
            variant="rect"
            width="100%"
            height={100}
            style={{ marginTop: 20 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height="calc(100vh - 545px)"
            style={{ marginTop: 8 }}
          />
        </>
      ) : (
        <KTable
          columns={columnsToRender}
          data={filteredItems}
          hasFilter
          onSelectRows={setSelectedRows}
          initialSelectedRows={preSelected}
          tableHeight="100%"
        />
      )}
    </div>
  );
};

export default GroupViewUsersTable;
